import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import useSWR from 'swr';
import api, {getURL, ResolverTypes} from '../../api';
import {Season} from '../../utils/types';
import {useReservation} from '../../context/reservation';
import {Statuses, useIsMounted, useStatus} from '../../utils/hooks';
import {getNumberOfGuests, getSeasonsIds} from '../../utils/reservation';
import {PATHS} from '../../Routes';
import {FormTypes, LocationStateTaxes} from './TouristTaxesSetup/TouristTaxesSetup';
import {NONE_EXEMPTION_OPTION} from '../../utils/constants';

export const MIN_AGE = 1;
export const MAX_AGE = 110;

function fetchSeasonIfRequired(reservation: any = {}) {
  if (!reservation) {
    return null;
  }

  const highSeasonId = getSeasonsIds(reservation)[0];
  if (!highSeasonId) {
    return null;
  }

  return getURL(api.seasons.ENDPOINTS.one(highSeasonId));
}

type GuestsTaxes = {
  amount: number;
  calculate_only: boolean;
  guest_list: {age: number; total: number; exemption: string}[];
  id: string;
  reservation_id: string;
};

type TaxExemptionType = {
  name: string;
  id: string;
  stat_type: string;
};
function getTaxExemptionsAsOptions(data: Array<TaxExemptionType>) {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map(t => {
    return {
      value: t.id,
      label: t.name,
    };
  });
}

function getIsAgeValid(age?: string | number) {
  return age && age !== '' && Number(age) >= MIN_AGE && Number(age) <= MAX_AGE;
}

type UseTaxesProps = {
  onError: (error: Error) => void;
  disabled?: boolean;
};
function useTaxes({disabled, onError}: UseTaxesProps) {
  const history = useHistory();
  const location = useLocation<Record<string, unknown>>();
  const {t, i18n} = useTranslation();
  const {data: reservation, refreshReservation} = useReservation();
  const guestsCount = getNumberOfGuests(reservation);
  const isMounted = useIsMounted();
  const {isLoading, setStatus} = useStatus();

  const axemptionNoneWithTransOption = React.useMemo(
    () => ({
      ...NONE_EXEMPTION_OPTION,
      label: t(NONE_EXEMPTION_OPTION.label),
    }),
    [t],
  );

  const {
    data: taxExemptions,
    mutate: refreshTaxExemptions,
    isValidating: isLoadingTaxExemptions,
  } = useSWR<TaxExemptionType[]>(getURL(api.statTaxExemptions.ENDPOINTS.get()));
  const {data: highSeason, isValidating: isLoadingSeason} = useSWR<Season>(
    fetchSeasonIfRequired(reservation),
  );

  React.useEffect(() => {
    if (!disabled) {
      refreshTaxExemptions();
    }
  }, [disabled, i18n.language, refreshTaxExemptions]);

  const reservationId = reservation.id;
  const {data: guestsTaxesList, mutate: refreshGuestsTaxesList} = useSWR<GuestsTaxes[]>(
    reservationId &&
      getURL(api.seasonGuests.ENDPOINTS.all(`reservation_id=${reservationId}`)),
    {
      onError: error => {
        if (!isMounted.current) {
          return;
        }

        if (error) {
          onError(error);
          return null;
        }
      },
    },
  );
  const guestsTaxes = React.useMemo(() => {
    if (!guestsTaxesList?.[0]) return;
    return {
      ...guestsTaxesList[0],
      guest_list: guestsTaxesList[0]?.guest_list?.slice(0, guestsCount),
    };
  }, [guestsCount, guestsTaxesList]);

  const hasSeasonExemptions = Boolean(highSeason?.exemptions?.length);

  const getUpdatedLocalLocationState = React.useCallback(
    (newData?: FormTypes) => ({
      ...location.state,
      guestsTaxes: newData?.guestsTaxes,
    }),
    [location.state],
  );

  const getTaxCalculationPayload = React.useCallback(
    (updatedLocationState: LocationStateTaxes, calculateOnly = false) => {
      return {
        calculate_only: calculateOnly,
        reservation_id: reservation?.id,
        guest_list: updatedLocationState?.guestsTaxes
          ?.filter(guest => guest?.age)
          .map(guest => {
            return {
              ...guest,
              exemption: guest.exemption?.value || axemptionNoneWithTransOption.value,
            };
          }),
      };
    },
    [axemptionNoneWithTransOption.value, reservation?.id],
  );

  const getMarkReservationAsPaidPayload = () => {
    return {
      have_taxes_been_paid: true,
    };
  };

  const markReservationAsPaid = async () => {
    const payload = getMarkReservationAsPaidPayload();
    const {data, error} = await api.reservations.patch(reservation?.id, payload);
    await refreshReservation();
    await refreshGuestsTaxesList();

    if (!isMounted.current) {
      return;
    }

    if (error) {
      onError(error);
      setStatus(Statuses.idle);
      return;
    }

    const isSomePaymentss = Boolean(parseFloat(data?.total_amount_to_pay || '0'));
    const nextPath = isSomePaymentss ? PATHS.pay : PATHS.finish;
    history.push(nextPath, location.state);
  };

  const checkIsNotFilledGuestsPayload = (
    guestsListPayload?: {age: string | number; exemption: string}[],
  ) => {
    const guestsCountPayload = guestsListPayload?.length;
    if (!guestsCountPayload) return true;
    const isNotFilledGuestsPayload = guestsListPayload.some(
      guest => !getIsAgeValid(guest.age),
    );
    return isNotFilledGuestsPayload || guestsCount !== guestsCountPayload;
  };

  const calculateAndSetGuestTax = async (formData: FormTypes, totalPrice: number) => {
    setStatus(Statuses.loading);
    const existingGuestList = guestsTaxesList;
    if (existingGuestList === null) {
      setStatus(Statuses.idle);
      return;
    }

    let result: ResolverTypes;
    const payload = getTaxCalculationPayload(getUpdatedLocalLocationState(formData));

    if (checkIsNotFilledGuestsPayload(payload.guest_list)) {
      onError(t('error_continue_without_taxes'));
      setStatus(Statuses.idle);
      return;
    }

    if (existingGuestList?.length) {
      result = await api.seasonGuests.patch(existingGuestList[0]?.id, payload);
    } else {
      result = await api.seasonGuests.post(payload);
    }

    if (!isMounted.current) {
      return;
    }

    if (result.error) {
      setStatus(Statuses.idle);
      onError(result.error);
      return;
    }

    if (result.data?.amount === 0) {
      markReservationAsPaid();
      return;
    }

    await refreshReservation();
    await refreshGuestsTaxesList();

    setStatus(Statuses.idle);

    if (!result.data?.amount) {
      onError(t('calced_price_not_found'));
      return;
    }

    if (result?.data?.amount !== Number(totalPrice.toFixed(2))) {
      onError(t('calced_price_is_incorrect'));
      return;
    }

    history.push(PATHS.pay, location.state);
  };

  const calculateAllGuestTax = React.useCallback(
    async (formData: FormTypes) => {
      const payload = getTaxCalculationPayload(
        getUpdatedLocalLocationState(formData),
        true,
      );

      const {data, error} = await api.seasonGuests.post<GuestsTaxes>(payload);

      if (!isMounted.current) {
        return;
      }

      if (error) return onError(error);

      return data;
    },
    [getTaxCalculationPayload, getUpdatedLocalLocationState, isMounted, onError],
  );

  const availableTaxExemptionsOptions = React.useMemo(() => {
    if (!taxExemptions || !highSeason) {
      return [axemptionNoneWithTransOption];
    }
    if (!hasSeasonExemptions) {
      return [axemptionNoneWithTransOption];
    }

    const seasonExemptions = highSeason.exemptions;
    const availableExemptions = taxExemptions?.filter(exemption => {
      return seasonExemptions.find(
        seasonExemption => seasonExemption.exemption_type === exemption.id,
      );
    });

    return [
      axemptionNoneWithTransOption,
      ...getTaxExemptionsAsOptions(availableExemptions),
    ];
  }, [axemptionNoneWithTransOption, hasSeasonExemptions, highSeason, taxExemptions]);

  return {
    guestsTaxes,
    refreshGuestsTaxes: refreshGuestsTaxesList,
    isLoading: isLoading || isLoadingSeason || isLoadingTaxExemptions,
    calculateAndSetGuestTax,
    calculateAllGuestTax,
    getUpdatedLocalLocationState,
    availableTaxExemptionsOptions,
  };
}

export {useTaxes, getIsAgeValid};
